import React, { useCallback, useState } from 'react';
import { FieldLabel } from '@puckeditor/core';
import type { CustomField } from '@puckeditor/core';

import { fieldControlStyle } from './field-control-style';

/**
 * Value shape for the combined alt-text/decorative control.
 *
 * Modelled as a single object so the two travel together and can never disagree (e.g. a
 * decorative image that also carries stale alt text).
 *
 * {@link resolveAltText} is shared by Image, Slideshow, and Split Banner so malformed nested
 * values cannot crash either the editor control or a production renderer. Slideshow and Split
 * Banner also use it to tolerate their existing plain-string alt values.
 */
export type AltTextValue = {
  alt: string;
  decorative: boolean;
};

export const DEFAULT_ALT_TEXT_VALUE: AltTextValue = { alt: '', decorative: false };

/**
 * Normalises a saved `alt` value to an `AltTextValue`. Slideshow and Split Banner retain
 * plain-string alt values from before the combined accessibility control, while Image supplies
 * the nested `alt` value. Puck does not inject `defaultProps` in production, so all
 * three renderers must share this field-level malformed-data guard.
 */
export function resolveAltText(value: unknown): AltTextValue {
  if (typeof value === 'string') return { alt: value, decorative: false };

  // Saved page JSON is unvalidated: `null`, arrays, numbers, and partial/mistyped objects (e.g.
  // `{ decorative: true }` with no `alt` key, or `{ alt: null }`) have all been observed in the
  // wild. None of those are safe to pass through as-is -- the published `Render` path has no
  // error boundary, so a single malformed block would white-screen the whole page the moment a
  // consumer touches `.alt.trim()` or `.decorative`. Coerce field-by-field instead of trusting
  // the shape.
  if (typeof value !== 'object' || value === null) return DEFAULT_ALT_TEXT_VALUE;

  const candidate = value as Partial<Record<keyof AltTextValue, unknown>>;
  const alt = typeof candidate.alt === 'string' ? candidate.alt : '';
  const decorative = candidate.decorative === true;

  return { alt, decorative };
}

type AltTextFieldProps = {
  field: { label?: string };
  name: string;
  value: unknown;
  onChange: (value: AltTextValue) => void;
  readOnly?: boolean;
  labelTooltip?: string;
};

const inputStyle: React.CSSProperties = {
  ...fieldControlStyle,
};

const checkboxRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 8,
  minHeight: 32,
  width: '100%',
  cursor: 'pointer',
};

const checkboxLabelStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--dcm-cms-alt-text-label-color, #646d72)',
};

/**
 * Puck custom field combining alt text with a "This image is decorative" checkbox.
 * The outer FieldLabel must be a div: its default label would nest the checkbox's own label,
 * breaking the native association when the markup is parsed by the browser.
 *
 * Ticking decorative disables (and visually de-emphasises) the alt input — decorative images
 * render `alt=""` regardless of whatever text was previously entered, so the disabled input
 * communicates that the text is currently inert without discarding it (unticking restores it).
 */
export function AltTextField({ field, name, value, onChange, readOnly, labelTooltip }: AltTextFieldProps): JSX.Element {
  const current = resolveAltText(value);
  const tooltipId = `alt-text-tooltip-${name}`;
  const [helpHovered, setHelpHovered] = useState(false);
  const [helpFocused, setHelpFocused] = useState(false);

  const handleAltChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly || current.decorative) return;
      onChange({ ...current, alt: e.currentTarget.value });
    },
    [current, onChange, readOnly]
  );

  const handleDecorativeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly) return;
      onChange({ ...current, decorative: e.currentTarget.checked });
    },
    [current, onChange, readOnly]
  );

  return (
    <FieldLabel
      label={field.label ?? name}
      readOnly={readOnly}
      el='div'
      icon={
        labelTooltip ? (
          <span
            onMouseEnter={() => setHelpHovered(true)}
            onMouseLeave={() => setHelpHovered(false)}
            style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}
          >
            <button
              type='button'
              title={labelTooltip}
              aria-label='More info'
              aria-describedby={tooltipId}
              onFocus={() => setHelpFocused(true)}
              onBlur={() => setHelpFocused(false)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setHelpHovered(false);
                  setHelpFocused(false);
                }
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 16,
                height: 16,
                flexShrink: 0,
                border: 0,
                padding: 0,
                background: 'transparent',
                color: 'var(--dcm-cms-alt-text-help-color, var(--puck-color-text-subtle, #646d72))',
                cursor: 'help',
              }}
            >
              <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true' focusable='false'>
                <circle cx='8' cy='8' r='6.5' stroke='currentColor' />
                <path d='M8 7v4' stroke='currentColor' strokeWidth='1.5' />
                <circle cx='8' cy='4.5' r='.8' fill='currentColor' />
              </svg>
            </button>
            <span
              id={tooltipId}
              role='tooltip'
              hidden={!helpHovered && !helpFocused}
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 1,
                width: 220,
                padding: 8,
                boxSizing: 'border-box',
                border: '1px solid var(--dcm-cms-alt-text-help-border, var(--puck-color-border, #d0d4d8))',
                borderRadius: 'var(--dcm-cms-alt-text-help-radius, 4px)',
                background: 'var(--dcm-cms-alt-text-help-background, var(--puck-color-surface, #fff))',
                color: 'var(--dcm-cms-alt-text-help-text, var(--puck-color-text, #181b1f))',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: 1.4,
                whiteSpace: 'normal',
              }}
            >
              {labelTooltip}
            </span>
          </span>
        ) : undefined
      }
    >
      <input
        type='text'
        value={current.alt}
        onChange={handleAltChange}
        disabled={(readOnly ?? false) || current.decorative}
        placeholder='Describe the image'
        aria-label={`${field.label ?? name} alt text`}
        data-testid={`alt-text-input-${name}`}
        style={{ ...inputStyle, opacity: current.decorative ? 0.5 : 1 }}
      />

      <label style={checkboxRowStyle} htmlFor={`alt-text-decorative-${name}`}>
        <input
          id={`alt-text-decorative-${name}`}
          type='checkbox'
          checked={current.decorative}
          onChange={handleDecorativeChange}
          disabled={readOnly}
          data-testid={`alt-text-decorative-${name}`}
          style={{ width: 20, height: 20, flexShrink: 0, margin: 0, cursor: 'inherit' }}
        />
        <span style={checkboxLabelStyle}>Decorative in this placement</span>
      </label>
    </FieldLabel>
  );
}

/**
 * Builds a complete Puck `custom` alt-text field, including the `ai.schema` the AI plugin
 * requires for every `custom` field. Mirrors `selectField()`'s guard: a bare
 * `{ type: 'custom', render: AltTextField }` works in the editor UI but 400s the entire AI chat
 * request the moment the AI plugin reaches it, so the schema is baked in here rather than left to
 * each call site to remember.
 */
export function altTextField(label: string, aiInstructions?: string): CustomField<AltTextValue> {
  return {
    type: 'custom',
    label,
    render: AltTextField,
    ai: {
      schema: {
        type: 'object',
        required: ['alt', 'decorative'],
        properties: {
          alt: {
            type: 'string',
            description: 'Descriptive alt text for screen readers. Ignored when decorative is true.',
          },
          decorative: {
            type: 'boolean',
            description: "True if this image is purely decorative and should render alt=''.",
          },
        },
      },
      // Alt text is a short, single-field value with no useful intermediate state -- streaming it
      // token-by-token would just flash partial nonsense in the input. Mirrors `src`/`linkHref`.
      stream: false,
      ...(aiInstructions ? { instructions: aiInstructions } : {}),
    },
  };
}
